const DEFAULT_THROTTLE_MS = 60_000;
const MAX_TRACKED_KEYS = 500;
const STALE_KEY_MULTIPLIER = 10;

type ThrottleState = { lastSentAt: number; lastSeenAt: number; suppressedCount: number };
type ThrottleStatus = { throttled: boolean; suppressedCount: number };
type ThrottleController = {
  check: (key: string, throttleMs?: number) => ThrottleStatus;
  commitSuccess: (key: string, throttleMs?: number) => void;
};

/**
 * Creates an independent throttle gate keyed by string.
 * Returns a controller with:
 * - `check`: read whether an event should be throttled
 * - `commitSuccess`: commit a successful send
 * Each call site (RUM, Datadog Logs, etc.) should create its own instance.
 */
export const createThrottle = (): ThrottleController => {
  const state = new Map<string, ThrottleState>();

  const cleanup = (now: number, throttleMs: number) => {
    const staleBefore = now - throttleMs * STALE_KEY_MULTIPLIER;
    for (const [k, s] of state.entries()) {
      if (s.lastSeenAt < staleBefore) state.delete(k);
    }
    while (state.size > MAX_TRACKED_KEYS) {
      const oldest = state.keys().next().value;
      if (!oldest) break;
      state.delete(oldest);
    }
  };

  return {
    check: (key: string, throttleMs: number = DEFAULT_THROTTLE_MS): ThrottleStatus => {
      const now = Date.now();
      const entry = state.get(key);

      if (entry && now - entry.lastSentAt < throttleMs) {
        entry.lastSeenAt = now;
        entry.suppressedCount++;
        return { throttled: true, suppressedCount: entry.suppressedCount };
      }

      return { throttled: false, suppressedCount: entry?.suppressedCount ?? 0 };
    },
    commitSuccess: (key: string, throttleMs: number = DEFAULT_THROTTLE_MS) => {
      const now = Date.now();
      state.set(key, { lastSentAt: now, lastSeenAt: now, suppressedCount: 0 });
      cleanup(now, throttleMs);
    }
  };
};
